import React from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Polygon, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const incidentIcon = L.divIcon({
  className: 'firewatch-incident-marker',
  html: '<div style="background:#1d4ed8;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 0 0 1px #1d4ed8"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const photoIcon = L.divIcon({
  className: 'firewatch-photo-marker',
  html: '<div style="background:#e85d04;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 0 0 1px #e85d04"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const destination = (lat, lng, bearingDeg, distanceKm) => {
  const R = 6371;
  const bearing = (bearingDeg * Math.PI) / 180;
  const lat1 = (lat * Math.PI) / 180;
  const lng1 = (lng * Math.PI) / 180;
  const angDist = distanceKm / R;
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angDist) +
      Math.cos(lat1) * Math.sin(angDist) * Math.cos(bearing),
  );
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(bearing) * Math.sin(angDist) * Math.cos(lat1),
      Math.cos(angDist) - Math.sin(lat1) * Math.sin(lat2),
    );
  return [(lat2 * 180) / Math.PI, (lng2 * 180) / Math.PI];
};

const buildFunnel = (photoPos, heading, distanceKm, fovDeg) => {
  if (!photoPos || heading === undefined || heading === null) return null;
  const half = fovDeg / 2;
  const reach = Math.max(distanceKm ? distanceKm * 1.3 : 1.0, 0.4);
  const steps = 12;
  const arc = [];
  for (let i = 0; i <= steps; i += 1) {
    const b = heading - half + (fovDeg * i) / steps;
    arc.push(destination(photoPos[0], photoPos[1], b, reach));
  }
  return [photoPos, ...arc, photoPos];
};

const PhotoMap = ({ photo, incident, distanceKm, fovDeg = 60, height = '100%' }) => {
  const photoPos = photo?.gps && [photo.gps.lat, photo.gps.lng];
  const incidentPos = incident && incident.lat && incident.lng && [incident.lat, incident.lng];
  const heading = photo?.gps?.heading_deg;
  const funnel = buildFunnel(photoPos, heading, distanceKm, fovDeg);
  const centerLine = photoPos && heading !== undefined && heading !== null
    ? [photoPos, destination(photoPos[0], photoPos[1], heading, Math.max(distanceKm ? distanceKm * 1.3 : 1.0, 0.4))]
    : null;

  const center = photoPos || incidentPos || [39.5, -8.0];
  const bounds = photoPos && incidentPos ? [photoPos, incidentPos] : null;

  return (
    <MapContainer
      center={center}
      zoom={13}
      bounds={bounds || undefined}
      boundsOptions={{ padding: [40, 40] }}
      style={{ height, width: '100%' }}
      scrollWheelZoom={false}
    >
      <TileLayer
        attribution='&copy; OpenStreetMap'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {photoPos && funnel && (
        <Polygon
          positions={funnel}
          pathOptions={{ color: '#e85d04', weight: 1, fillColor: '#e85d04', fillOpacity: 0.18 }}
        />
      )}
      {centerLine && (
        <Polyline positions={centerLine} color="#e85d04" weight={2} dashArray="4 4" />
      )}
      {incidentPos && (
        <Marker position={incidentPos} icon={incidentIcon}>
          <Popup>Incidente {incident.api_id}<br />{incident.location}</Popup>
        </Marker>
      )}
      {photoPos && (
        <Marker position={photoPos} icon={photoIcon}>
          <Popup>
            Foto
            {heading !== undefined && heading !== null && (
              <><br />Azimute {Math.round(heading)}°</>
            )}
          </Popup>
        </Marker>
      )}
      {photoPos && incidentPos && (
        <Polyline positions={[photoPos, incidentPos]} color="#6b7280" dashArray="6 6" />
      )}
      {photoPos && funnel && funnel.length > 2 && (
        <>
          <CircleMarker center={funnel[1]} radius={3} color="#e85d04" fillOpacity={1} />
          <CircleMarker center={funnel[funnel.length - 2]} radius={3} color="#e85d04" fillOpacity={1} />
        </>
      )}
    </MapContainer>
  );
};

export default PhotoMap;
